"use client";

import {
  useState,
  useEffect,
  forwardRef,
  createContext,
  useContext,
  useMemo,
  CSSProperties,
  ButtonHTMLAttributes,
  HTMLAttributes,
} from "react";

export type SpinnerName =
  | "braille"
  | "braillewave"
  | "dna"
  | "scan"
  | "rain"
  | "scanline"
  | "line"
  | "pulse"
  | "snake"
  | "sparkle"
  | "cascade"
  | "columns"
  | "orbit"
  | "breathe"
  | "waverows"
  | "checkerboard"
  | "helix"
  | "fillsweep"
  | "diagswipe";

export interface SpinnerDef {
  frames: readonly string[];
  interval: number;
}

const SPINNERS: Record<SpinnerName, SpinnerDef> = {
  braille: {
    frames: ["⠋","⠙","⠹","⠸","⠼","⠴","⠦","⠧","⠇","⠏"],
    interval: 80,
  },
  braillewave: {
    frames: ["⠁⠂⠄⡀","⠂⠄⡀⢀","⠄⡀⢀⠠","⡀⢀⠠⠐","⢀⠠⠐⠈","⠠⠐⠈⠁","⠐⠈⠁⠂","⠈⠁⠂⠄"],
    interval: 100,
  },
  dna: {
    frames: ["⠋⠉⠙⠚","⠉⠙⠚⠒","⠙⠚⠒⠂","⠚⠒⠂⠂","⠒⠂⠂⠒","⠂⠂⠒⠲","⠂⠒⠲⠴","⠒⠲⠴⠤","⠲⠴⠤⠄","⠴⠤⠄⠋","⠤⠄⠋⠉","⠄⠋⠉⠙"],
    interval: 80,
  },
  scan: {
    frames: ["⠀⠀⠀⠀","⡇⠀⠀⠀","⣿⠀⠀⠀","⢸⡇⠀⠀","⠀⣿⠀⠀","⠀⢸⡇⠀","⠀⠀⣿⠀","⠀⠀⢸⡇","⠀⠀⠀⣿","⠀⠀⠀⢸"],
    interval: 70,
  },
  rain: {
    frames: ["⢁⠂⠔⠈","⠂⠌⡠⠐","⠄⡐⢀⠡","⡈⠠⠀⢂","⠐⢀⠁⠄","⠠⠁⠊⡀","⢁⠂⠔⠈","⠂⠌⡠⠐","⠄⡐⢀⠡","⡈⠠⠀⢂","⠐⢀⠁⠄","⠠⠁⠊⡀"],
    interval: 100,
  },
  scanline: {
    frames: ["⠉⠉⠉","⠛⠛⠛","⠿⠿⠿","⣿⣿⣿","⣶⣶⣶","⣤⣤⣤","⣀⣀⣀","⠀⠀⠀"],
    interval: 120,
  },
  line: {
    frames: ["⠀⠀⠀⠀","⠁⠁⠁⠁","⠂⠂⠂⠂","⠄⠄⠄⠄","⡀⡀⡀⡀","⠀⠀⠀⠀","⡀⡀⡀⡀","⠄⠄⠄⠄","⠂⠂⠂⠂","⠁⠁⠁⠁"],
    interval: 70,
  },
  pulse: {
    frames: ["⠀⠶⠀","⠰⣿⠆","⢾⣉⡷","⣏⠀⣹","⡁⠀⢈"],
    interval: 180,
  },
  snake: {
    frames: ["⣁⡀","⣉⠀","⡉⠁","⠉⠉","⠈⠙","⠀⠛","⠐⠚","⠒⠒","⠖⠂","⠶⠀","⠦⠄","⠤⠤","⠠⢤","⠀⣤","⢀⣠","⣀⣀"],
    interval: 80,
  },
  sparkle: {
    frames: ["⡡⠊⢔⠡","⠊⡰⡡⡘","⢔⢅⠈⢢","⡁⢂⠆⡍","⢔⠨⢑⢐","⠨⡑⡠⠊"],
    interval: 150,
  },
  cascade: {
    frames: ["⠀⠀⠀⠀","⠀⠀⠀⠀","⠁⠀⠀⠀","⠋⠀⠀⠀","⠞⠁⠀⠀","⡴⠋⠀⠀","⣠⠞⠁⠀","⢀⡴⠋⠀","⠀⣠⠞⠁","⠀⢀⡴⠋","⠀⠀⣠⠞","⠀⠀⢀⡴","⠀⠀⠀⣠","⠀⠀⠀⢀"],
    interval: 60,
  },
  columns: {
    frames: ["⡀⠀⠀","⡄⠀⠀","⡆⠀⠀","⡇⠀⠀","⣇⠀⠀","⣧⠀⠀","⣷⠀⠀","⣿⠀⠀","⣿⡀⠀","⣿⡄⠀","⣿⡆⠀","⣿⡇⠀","⣿⣇⠀","⣿⣧⠀","⣿⣷⠀","⣿⣿⠀","⣿⣿⡀","⣿⣿⡄","⣿⣿⡆","⣿⣿⡇","⣿⣿⣇","⣿⣿⣧","⣿⣿⣷","⣿⣿⣿","⣿⣿⣿","⠀⠀⠀"],
    interval: 60,
  },
  orbit: {
    frames: ["⠃","⠉","⠘","⠰","⢠","⣀","⡄","⠆"],
    interval: 100,
  },
  breathe: {
    frames: ["⠀","⠂","⠌","⡑","⢕","⢝","⣫","⣟","⣿","⣟","⣫","⢝","⢕","⡑","⠌","⠂","⠀"],
    interval: 100,
  },
  waverows: {
    frames: ["⠖⠉⠉⠑","⡠⠖⠉⠉","⣠⡠⠖⠉","⣄⣠⡠⠖","⠢⣄⣠⡠","⠙⠢⣄⣠","⠉⠙⠢⣄","⠊⠉⠙⠢","⠜⠊⠉⠙","⡤⠜⠊⠉","⣀⡤⠜⠊","⢤⣀⡤⠜","⠣⢤⣀⡤","⠑⠣⢤⣀","⠉⠑⠣⢤","⠋⠉⠑⠣"],
    interval: 90,
  },
  checkerboard: {
    frames: ["⢕⢕⢕","⡪⡪⡪","⢊⠔⡡","⡡⢊⠔"],
    interval: 250,
  },
  helix: {
    frames: ["⢌⣉⢎⣉","⣉⡱⣉⡱","⣉⢎⣉⢎","⡱⣉⡱⣉","⢎⣉⢎⣉","⣉⡱⣉⡱","⣉⢎⣉⢎","⡱⣉⡱⣉","⢎⣉⢎⣉","⣉⡱⣉⡱","⣉⢎⣉⢎","⡱⣉⡱⣉","⢎⣉⢎⣉","⣉⡱⣉⡱","⣉⢎⣉⢎","⡱⣉⡱⣉"],
    interval: 80,
  },
  fillsweep: {
    frames: ["⣀⣀","⣤⣤","⣶⣶","⣿⣿","⣿⣿","⣿⣿","⣶⣶","⣤⣤","⣀⣀","⠀⠀","⠀⠀"],
    interval: 100,
  },
  diagswipe: {
    frames: ["⠁⠀","⠋⠀","⠟⠁","⡿⠋","⣿⠟","⣿⡿","⣿⣿","⣿⣿","⣾⣿","⣴⣿","⣠⣾","⢀⣴","⠀⣠","⠀⢀","⠀⠀","⠀⠀"],
    interval: 60,
  },
};

export const spinnerNames = Object.keys(SPINNERS) as SpinnerName[];

function getReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function useSpinner(
  name: SpinnerName,
  speed: number = 1,
  paused: boolean = false,
  ignoreReducedMotion: boolean = false
): string {
  const def = SPINNERS[name];
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!ignoreReducedMotion && getReducedMotion()) return;
    if (paused) return;
    setFrame(0);
    const ms = def.interval / Math.max(speed, 0.01);
    const id = setInterval(() => {
      setFrame((prev) => (prev + 1) % def.frames.length);
    }, ms);
    return () => clearInterval(id);
  }, [name, speed, paused, ignoreReducedMotion, def.interval, def.frames.length]);

  return def.frames[frame] ?? def.frames[0];
}

export interface BaseSpinnerProps {
  name?: SpinnerName;
  color?: string;
  size?: string | number;
  speed?: number;
  paused?: boolean;
  ignoreReducedMotion?: boolean;
  className?: string;
  style?: CSSProperties;
  label?: string;
}

export type SpinnerProps = BaseSpinnerProps &
  Omit<HTMLAttributes<HTMLSpanElement>, keyof BaseSpinnerProps>;

export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(
  function Spinner(
    {
      name = "braille",
      color,
      size,
      speed = 1,
      paused,
      ignoreReducedMotion,
      className,
      style,
      label,
      ...rest
    },
    ref
  ) {
    const frame = useSpinner(
      name,
      speed,
      paused ?? false,
      ignoreReducedMotion ?? false
    );

    return (
      <span
        ref={ref}
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "baseline",
          lineHeight: 1,
          position: "relative",
          ...style,
        }}
        {...rest}
      >
        <span
          aria-hidden="true"
          style={{
            fontFamily: "monospace",
            lineHeight: 1,
            userSelect: "none",
            whiteSpace: "pre",
            color: color ?? "currentColor",
            fontSize: size,
          }}
        >
          {frame}
        </span>
        {label && (
          <span role="status" aria-live="polite" style={{
            position: "absolute",
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            whiteSpace: "nowrap",
            border: 0,
          }}>
            {label}
          </span>
        )}
      </span>
    );
  }
);

export interface SpinnerInlineProps extends BaseSpinnerProps {
  children?: React.ReactNode;
  gap?: string | number;
}

export function SpinnerInline({
  children,
  gap = "0.4em",
  ...spinnerProps
}: SpinnerInlineProps) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap }}>
      <Spinner {...spinnerProps} />
      {children ? <span>{children}</span> : null}
    </span>
  );
}
